import { SortableHeader } from '@/components/search/SortableHeader'
import './systemlogs.css'

import React, { useRef } from 'react'
import { Services } from '@/services/services'
import { CurrentSession } from '@/services/repository/session'
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout'
import { PageQuery, PageResponse, SearchTable, SearchTableParams, SearchTableRef } from '@/components/search/SearchTable'
import { formatDate } from '@/common/time'
import { useResponsiveChange } from '@/common/device'
import { BackButton } from '@/components/layout/NavBar'
import { useModal } from '@/components/modal/Modal'
import { useAppContext } from '@/context/app'
import { Spinner } from '@/components/spinner'

export interface SystemLog {
  logId: string 
  userId: string 
  eventType: string
  username: string
  data: any 
  createdAt: string
}

type DetailsSystemLogsPopupProps = {
  services: Services
  session: CurrentSession
  log: SystemLog
  onClose: () => void
}

export const DetailsSystemLogsPopup = ({ session, log, onClose }: DetailsSystemLogsPopupProps) => {
  return (
    <div className="systemlogs-popup">
      <div className="popup-header">
        <div className="title">
          {session.translates.for('elements.table.systemlogs.details')}
        </div>
      </div>

      <div className="popup-body">
        <div className="formgrid">
          <div className="label">{session.translates.for('elements.table.systemlogs.id')}</div>
          <div className="value">{log.logId || '-'}</div>

          <div className="label">{session.translates.for('elements.table.systemlogs.created_at')}</div>
          <div className="value">{log.createdAt ? formatDate(new Date(log.createdAt)) : '-'}</div>

          <div className="label">{session.translates.for('elements.table.systemlogs.event_type')}</div>
          <div className="value">{log.eventType}</div>

          <div className="label">{session.translates.for('elements.table.systemlogs.user_id')}</div>
          <div className="value">{log.userId || '-'}</div>

          <div className="label">{session.translates.for('elements.table.systemlogs.data')}</div>
          <pre className="value pre">{JSON.stringify(log.data ?? null, null, 2)}</pre>
        </div>
      </div>
    </div>
  )
}

export const SystemLogPage = () => {
  const { services, session, translates } = useAppContext()
  const searchTableRef = useRef<SearchTableRef>(null)
  const { openModal } = useModal()

  useResponsiveChange()

  if(!translates || !session) {
    return <Spinner />
  }
  const openUserDetails = (userId: string) => {
    const url = `${window.location.origin}/communal/statistics/users?length=1d&user=${userId}`;
    window.open(url, '_blank');
  }

  const tableParams: SearchTableParams<SystemLog> = {
    fetch: (q: PageQuery): Promise<PageResponse<SystemLog>> => {
      const params = new URLSearchParams({
        pageNumber: q.pageNumber.toString(),
        pageSize: q.pageSize.toString(),
        search: q.search || '',
        sortBy: q.sortBy || '',
        sortDirection: q.sortDirection || '',
      })

      return services.session
        .fetchWithToken(`/api/communal/systemlogs/search?${params.toString()}`)
        .then((r) => (r.status === 200 ? r.json() : Promise.reject()))
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 30,
      search: '',
    },
    toolbox: () => <></>,
    defaultSortBy: 'createdAt',
    header: (query, onSort) => (
      <>
        <SortableHeader field='createdAt' query={query} onSort={onSort}>{session.translates.for('elements.table.systemlogs.created_at')}</SortableHeader>
        <SortableHeader field='eventType' query={query} onSort={onSort}>{session.translates.for('elements.table.systemlogs.event_type')}</SortableHeader>
        <SortableHeader field='username' query={query} onSort={onSort}>{session.translates.for('elements.table.systemlogs.username')}</SortableHeader>
        <div className="hcol"></div>
      </>
    ),
    row: (row) => (
      <>
        <div className="col">{row.createdAt ? formatDate(new Date(row.createdAt)) : '-'}</div>
        <div className="col">{row.eventType}</div>
        <div className="col" onClick={() => openUserDetails(row.userId)}>{row.username}</div>
        <div
          className="col icocol"
          onClick={() =>
            openModal((props) => (
              <DetailsSystemLogsPopup
                {...props}
                services={services}
                session={session}
                log={row}
              />
            ))
          }
          title="Details"
        >
          <i className="svgfont-next" />
        </div>
      </>
    ),
    translates: session.translates,
  }

  return (
    <LoggedInPageLayout
      services={services}
      session={session}
      pageName="superadmin-systemlogs"
      navbarLeft={<BackButton services={services} translates={translates} />}
    >
      <SearchTable ref={searchTableRef} {...tableParams} />
    </LoggedInPageLayout>
  )
}